import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, Network, Tags, Inbox, ArrowRight } from 'lucide-react';
import { useCatalogsStore } from '@/stores/catalogs';
import { CatalogCard } from '@/components/knowledge/catalogs/CatalogCard';

export function CatalogIndexPage() {
  const { summary, fetchSummary } = useCatalogsStore();

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const totalPending =
    summary.pending_by_source.uos +
    summary.pending_by_source.concept_type +
    summary.pending_by_source.ad_taxonomy;

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div>
          <h1 className="text-xl font-semibold text-navy-900">Canonical Catalogs</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Governance surfaces for UOS, ConceptType, and Ad Taxonomy
          </p>
        </div>
        <Link
          to="/knowledge/catalogs/proposed"
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 transition-colors shadow-sm"
        >
          <Inbox size={14} />
          Proposed Queue
          {totalPending > 0 && (
            <span className="ml-1 text-[10px] font-bold bg-navy-900/10 text-navy-900 px-1.5 py-0.5 rounded-full">
              {totalPending}
            </span>
          )}
          <ArrowRight size={14} />
        </Link>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl">
          <CatalogCard
            title="Universal Ontological Schema"
            subtitle="Philosophical classification of all canonical knowledge"
            href="/knowledge/catalogs/ontology"
            icon={GitBranch}
            badgeLabel="UOS"
            pendingCount={summary.pending_by_source.uos}
            stats={[
              { label: 'Classes', value: summary.uos_classes },
              { label: 'Classified', value: summary.uos_classified },
              {
                label: 'Pending',
                value: summary.pending_by_source.uos,
                highlight: summary.pending_by_source.uos > 0,
              },
            ]}
          />

          <CatalogCard
            title="Concept Taxonomy"
            subtitle="Dynamic IS-A hierarchy of market and consumer concepts"
            href="/knowledge/catalogs/concepts"
            icon={Network}
            badgeLabel="ConceptType"
            pendingCount={summary.pending_by_source.concept_type}
            stats={[
              { label: 'Nodes', value: summary.concept_types },
              {
                label: 'Pending',
                value: summary.pending_by_source.concept_type,
                highlight: summary.pending_by_source.concept_type > 0,
              },
              { label: 'Status', value: summary.concept_types > 0 ? 'active' : 'empty' },
            ]}
          />

          <CatalogCard
            title="Ad Taxonomy"
            subtitle="Form and Meaning dimensions of ad creative performance"
            href="/knowledge/catalogs/ad-taxonomy"
            icon={Tags}
            badgeLabel="Ad Taxonomy"
            pendingCount={summary.pending_by_source.ad_taxonomy}
            stats={[
              { label: 'Form', value: summary.ad_taxonomy_form },
              { label: 'Meaning', value: summary.ad_taxonomy_meaning },
              {
                label: 'Pending',
                value: summary.pending_by_source.ad_taxonomy,
                highlight: summary.pending_by_source.ad_taxonomy > 0,
              },
            ]}
          />
        </div>

        {!summary.loaded && (
          <p className="mt-6 text-xs text-slate-400 font-mono">loading catalog counts...</p>
        )}
      </div>
    </div>
  );
}
